import React, { useRef, useState } from 'react';
import { Avatar } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { ActivityValueSnapshot, TaskActivity } from '../../../../types/TaskActivity';
import StatusLabel, { StatusComponentType } from '../../../../components/UI/StatusLabel';
import { SprintStatusLabel } from '../../../../components/UI/SprintStatusLabel';
import LabelChip, { LabelChipType } from '../../../../components/UI/LabelChip';
import { TaskEstimateComponentType, TaskEstimateLabel } from '../../../../components/UI/TaskEstimateLabel';
import { PriorityLabel } from '../../../../components/UI/Dropdown/PriorityDropdown';
import { PriorityOptions } from '../../../../components/TaskTable/helpers/helper';
import { TaskTypeLabel } from '../../../../components/UI/Dropdown/TaskTypeDropdown';
import { TaskType } from '../../../../types/TaskType';
import { TaskStatusGroup } from '../../../../types/TaskStatus';
import { TaskStage } from '../../../../types/Task';
import { WorkspaceEstimationType } from '../../../../types/Workspace';
import { getSprintStatus } from '../../../../utils/taskListUtils';
import { StatusColor } from '../../../../constants/design_system/statusColors';
import colorTokens from '../../../../constants/design_system/colors';
import { EpicIcon } from '../../../../assets/CustomIcons/EpicIcon';
import { getUserProfilePictureUrl } from '../../../../common/userHelper';
import { useGetCurrentWorkspaceUsers } from '../../../../serverState/workspace';
import { useNavigateToTask } from '../../../../hooks/useTaskNavigation';
import CustomTooltip from '../../../../components/UI/Tooltip/CustomTooltip';

// Mirror of server FIELD_LABELS (server/src/modules/task/activity/activity.types.ts).
const FIELD_LABELS: Record<string, string> = {
  type: 'Type',
  stage: 'Stage',
  statusId: 'Status',
  status: 'Status',
  priority: 'Priority',
  assignee: 'Owner',
  storyPoints: 'Estimate',
  estimate: 'Estimate',
  startDate: 'Start Date',
  deadline: 'Target Date',
  sprintId: 'Sprint',
  epicId: 'Epic',
  parentTaskId: 'Parent',
  title: 'Title',
  blockingTaskId: 'Blocking',
  blockedTaskId: 'Blocked by',
};

/** The workspace-scoped identifiers a snapshot needs before it can be linked. */
type TaskRef = { workspaceId: number; taskId: number };

// Activity rows recorded before the server started emitting meta have none, so every
// chip degrades to its static form rather than rendering a dead link. (#2615)
const taskRefFromMeta = (snap: ActivityValueSnapshot): TaskRef | undefined => {
  const taskId = snap.meta?.taskId;
  const workspaceId = snap.meta?.workspaceId;
  return typeof taskId === 'number' && typeof workspaceId === 'number' ? { workspaceId, taskId } : undefined;
};

// The chips carry the full value on a tooltip, but only once the text is really
// clipped — tooltipping a value that is fully visible with a copy of itself is
// noise, which is what QA kept reporting on the Inbox card (#3002). The width a
// chip gets depends on where it lands (a task-view row is far wider than a
// sidebar card), so the check has to be a measurement on hover, not a guess at
// render time. Ref goes on the inner `truncate` span; the listener goes on the
// tooltip's anchor so the measurement is done by the time the tooltip could open.
const useTruncated = () => {
  const ref = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const measure = () => {
    if (ref.current) setIsTruncated(ref.current.scrollWidth > ref.current.clientWidth);
  };
  return { ref, isTruncated, measure };
};

// `text-overflow: ellipsis` only applies to a block-ish box, so every chip puts
// `truncate` on an inner span and caps the width on the flex container around it —
// otherwise long names clip mid-character. The tooltip carries the full value. (#2615)
const PlainChip: React.FC<{ text: string }> = ({ text }) => {
  const { ref, isTruncated, measure } = useTruncated();
  return (
    <CustomTooltip title={isTruncated ? text : ''}>
      <span
        className="inline-flex items-center h-5 px-2 rounded bg-neutral-200 text-brand-grey-dark text-sm font-normal max-w-[180px]"
        onMouseEnter={measure}
      >
        <span ref={ref} className="truncate">
          {text}
        </span>
      </span>
    </CustomTooltip>
  );
};

// Mentions the related task with the same visual language as Notifications
// (underlined, clickable, navigates to the task) instead of a plain static chip. (#2615)
const TaskMentionLink: React.FC<{ workspaceId: number; taskId: number; title: string }> = ({
  workspaceId,
  taskId,
  title,
}) => {
  const navigateToTask = useNavigateToTask();
  const { ref, isTruncated, measure } = useTruncated();
  return (
    <CustomTooltip title={isTruncated ? title : ''}>
      <span
        className="inline-flex items-center max-w-[180px] text-brand-grey-dark text-sm font-normal underline cursor-pointer"
        onMouseEnter={measure}
        onClick={(e) => {
          e.stopPropagation();
          navigateToTask({ workspaceId, taskId });
        }}
      >
        <span ref={ref} className="truncate">
          {title}
        </span>
      </span>
    </CustomTooltip>
  );
};

// Mirrors EpicLabel's icon chip (EpicDropdown.tsx) — purple square + EpicIcon + title.
// Only the title is ever clickable; the icon stays chrome.
const EpicChip: React.FC<{ text: string; onTitleClick?: (e: React.MouseEvent) => void }> = ({ text, onTitleClick }) => {
  const { ref, isTruncated, measure } = useTruncated();
  return (
    <CustomTooltip title={isTruncated ? text : ''}>
      <span
        className="inline-flex items-center gap-1 text-brand-grey-dark text-sm font-normal max-w-[220px]"
        onMouseEnter={measure}
      >
        <span
          className="h-4 w-4 border border-solid justify-center items-center flex shrink-0"
          style={{ backgroundColor: colorTokens.purple[500], borderColor: colorTokens.purple[300], borderRadius: 4 }}
        >
          <EpicIcon className="text-white" />
        </span>
        <span ref={ref} className={`truncate ${onTitleClick ? 'underline cursor-pointer' : ''}`} onClick={onTitleClick}>
          {text}
        </span>
      </span>
    </CustomTooltip>
  );
};

// Kept separate from EpicChip so a meta-less snapshot never pays for the router/query
// subscriptions useNavigateToTask pulls in.
const EpicChipLink: React.FC<{ text: string; linkTo: TaskRef }> = ({ text, linkTo }) => {
  const navigateToTask = useNavigateToTask();
  return (
    <EpicChip
      text={text}
      onTitleClick={(e) => {
        e.stopPropagation();
        navigateToTask(linkTo);
      }}
    />
  );
};

// The snapshot carries the owner's id and display name but no picture, so the avatar is resolved
// from the space's member list at render time. Reading it here rather than snapshotting it
// server-side also backfills rows written before the user had a picture. (#2748)
const OwnerChip: React.FC<{ name: string; userId: number | null }> = ({ name, userId }) => {
  const { ref, isTruncated, measure } = useTruncated();
  const workspaceUsersQuery = useGetCurrentWorkspaceUsers();
  const profilePicturePath =
    userId == null
      ? null
      : (workspaceUsersQuery.data?.find((access) => access.userId === userId)?.User?.profilePicturePath ?? null);

  return (
    <CustomTooltip title={isTruncated ? name : ''}>
      <span
        className="inline-flex items-center gap-1 text-brand-grey-dark text-sm font-normal max-w-[200px]"
        onMouseEnter={measure}
      >
        <Avatar
          icon={<UserOutlined />}
          size={16}
          src={getUserProfilePictureUrl(profilePicturePath)}
          style={{ backgroundColor: colorTokens.neutral[400], fontSize: 9 }}
        >
          {name?.trim()?.[0]?.toUpperCase()}
        </Avatar>
        <span ref={ref} className="truncate">
          {name}
        </span>
      </span>
    </CustomTooltip>
  );
};

/** Render a single FROM/TO value as the pill that matches the task property. */
export const valuePill = (field: string | null, snap: ActivityValueSnapshot | null): React.ReactNode => {
  if (!snap) return null;
  switch (field) {
    case 'stage':
      return (
        <SprintStatusLabel
          sprintStatus={getSprintStatus({ stage: snap.raw as TaskStage, isScrum: true })}
          component="dropdown"
        />
      );
    case 'statusId':
    case 'status':
      return (
        <StatusLabel
          group={TaskStatusGroup.notStarted}
          title={snap.display}
          color={(snap.color ?? 'grey') as StatusColor}
          component={StatusComponentType.Dropdown}
        />
      );
    case 'priority':
      return <PriorityLabel priority={snap.raw as PriorityOptions} size="medium" />;
    case 'type':
      return <TaskTypeLabel type={snap.raw as TaskType} size="medium" />;
    case 'storyPoints':
    case 'estimate':
      return (
        <TaskEstimateLabel
          estimate={Number(snap.raw)}
          component={TaskEstimateComponentType.dropdown}
          estimationType={WorkspaceEstimationType.storyPoints}
        />
      );
    case 'labelId':
      return (
        <LabelChip
          label={{
            id: Number(snap.raw) || 0,
            title: snap.display,
            color: (snap.color ?? 'grey') as StatusColor,
            workspaceId: 0,
            orderIndex: 0,
            createdAt: '',
            updatedAt: '',
          }}
          type={LabelChipType.Dropdown}
        />
      );
    case 'epicId': {
      const linkTo = taskRefFromMeta(snap);
      return linkTo ? <EpicChipLink text={snap.display} linkTo={linkTo} /> : <EpicChip text={snap.display} />;
    }
    case 'assignee': {
      const ownerId = Number(snap.raw);
      return <OwnerChip name={snap.display} userId={Number.isFinite(ownerId) ? ownerId : null} />;
    }
    case 'startDate':
    case 'deadline':
      return <span className="text-brand-grey-dark text-sm font-medium">{snap.display}</span>;
    case 'blockingTaskId':
    case 'blockedTaskId':
    case 'parentTaskId': {
      const linkTo = taskRefFromMeta(snap);
      return linkTo ? (
        <TaskMentionLink workspaceId={linkTo.workspaceId} taskId={linkTo.taskId} title={snap.display} />
      ) : (
        <PlainChip text={snap.display} />
      );
    }
    case 'title':
    default:
      return <PlainChip text={snap.display} />;
  }
};

const field = (a: TaskActivity): string => FIELD_LABELS[a.field ?? ''] ?? a.field ?? '';

/**
 * Render the part of the activity line that follows the actor's name, e.g.
 * "changed the Priority from [Low] to [Medium]". Pills are driven purely by the
 * server snapshot, so renamed/deleted entities still render correctly.
 */
export const renderActivityContent = (a: TaskActivity): React.ReactNode => {
  const from = valuePill(a.field, a.oldValue);
  const to = valuePill(a.field, a.newValue);

  switch (a.type) {
    case 'task_created':
      return <span>created the item</span>;
    case 'description_updated':
      return <span>updated description</span>;
    case 'comment_added':
      return <span>added a comment</span>;
    case 'link_added':
      return <span>added a link</span>;
    case 'attachment_added':
      return <span>added an attachment</span>;
    case 'label_added':
      return <span className="inline-flex items-center gap-1 flex-wrap">added the Label {to}</span>;
    case 'label_removed':
      return <span className="inline-flex items-center gap-1 flex-wrap">removed the Label {from}</span>;
    case 'dependency_added':
      return (
        <span className="inline-flex items-center gap-1 flex-wrap">
          added {field(a)} {to}
        </span>
      );
    case 'dependency_removed':
      return (
        <span className="inline-flex items-center gap-1 flex-wrap">
          removed {field(a)} {from}
        </span>
      );
    case 'field_changed': {
      // Assigned/added from empty.
      if (!a.oldValue && a.newValue) {
        if (a.field === 'epicId') {
          return <span className="inline-flex items-center gap-1 flex-wrap">assigned the Epic {to}</span>;
        }
        if (a.field === 'parentTaskId') {
          return <span className="inline-flex items-center gap-1 flex-wrap">added the Parent {to}</span>;
        }
        return (
          <span className="inline-flex items-center gap-1 flex-wrap">
            set the {field(a)} to {to}
          </span>
        );
      }
      // Cleared to empty.
      if (a.oldValue && !a.newValue) {
        return (
          <span className="inline-flex items-center gap-1 flex-wrap">
            removed the {field(a)} {from}
          </span>
        );
      }
      // Changed from one value to another.
      return (
        <span className="inline-flex items-center gap-1 flex-wrap">
          changed the {field(a)} from {from} to {to}
        </span>
      );
    }
    default:
      return null;
  }
};
